import {
  mapCargoOnboardFormToApi,
  shouldShowCargoOnboardSection,
} from "@features/vessel-call/cargoOnboard";
import {
  CALL_ADDITIONAL_DOCUMENT_TYPES,
  FORM_FIELDS,
  methodAtImo2020ComplianceValues,
  NATURE_OF_CALL,
  NATURE_OF_CALL_SPECIFY,
} from "@features/vessel-call/create/constants";
import type { VesselCallFormState } from "@features/vessel-call/create/store/useVesselCallForm.store";
import type { VesselCallCreateRequest } from "@services/vesselCall/types";

const toISODateTime = (v: unknown): string | undefined => {
  if (!v) return undefined;
  const d = v instanceof Date ? v : typeof v === "string" ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

const toISODate = (v: unknown): string | undefined => {
  const iso = toISODateTime(v);
  return iso ? iso.slice(0, 10) : undefined;
};

const mapAdditionalDocumentType = (value: unknown): string => {
  const normalized = String(value ?? "").trim();
  return (Object.values(CALL_ADDITIONAL_DOCUMENT_TYPES) as string[]).includes(normalized)
    ? normalized
    : "";
};

const mapAdditionalDocumentSpecify = (type: string, value: unknown): string | null => {
  if (type !== CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY) return null;

  const specify = typeof value === "string" ? value.trim() : "";
  return specify || null;
};

const mapFileFormat = (value: unknown, fileName: unknown): string => {
  const format = typeof value === "string" ? value.trim() : "";
  if (format && !format.includes("/")) return format.replace(/^\./, "").toUpperCase();

  const name = typeof fileName === "string" ? fileName : "";
  const extension = name.split(".").pop();
  return extension && extension !== name ? extension.toUpperCase() : "";
};

const toNumber = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const toNullableNumber = (v: unknown): number | null => {
  const n = toNumber(v);
  return typeof n === "number" ? n : null;
};

const toIntOrZero = (v: unknown): number => {
  const n = toNumber(v);
  return typeof n === "number" ? n : 0;
};

const toNullableInt = (v: unknown): number | null => {
  const n = toIntOrZero(v);
  return n === 0 ? null : n;
};

const toPositiveOrder = (value: unknown, fallbackIndex: number): number => {
  const numericValue = typeof value === "number" ? value : Number(value);

  if (Number.isInteger(numericValue) && numericValue > 0) {
    return numericValue;
  }

  return fallbackIndex + 1;
};

const mapNatureOfCall = (s: VesselCallFormState) => ({
  primaryNatureOfCallCode: s[NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_CODE] ?? "",
  primaryNatureOfCallDesc: s[NATURE_OF_CALL.PRIMARY_NATURE_OF_CALL_DESC] ?? "",
  cargoOperation: Boolean(s[NATURE_OF_CALL.CARGO_OPERATION]),
  cargoOperationSpecify: s[NATURE_OF_CALL_SPECIFY.CARGO_OPETATION_SPECIFY] ?? "",
  wasteDisposal: Boolean(s[NATURE_OF_CALL.WASTE_DISPOSAL]),
  wasteDisposalSpecify: s[NATURE_OF_CALL_SPECIFY.WASTE_DISPOSAL_SPECIFY] ?? "",
  others: Boolean(s[NATURE_OF_CALL.OTHERS]),
  otherSpecify: s[NATURE_OF_CALL_SPECIFY.OTHERS_SPECIFY] ?? "",
  sts: Boolean(s[NATURE_OF_CALL.STS]),
  bunkering: Boolean(s[NATURE_OF_CALL.BUNKERING]),
  crewChange: Boolean(s[NATURE_OF_CALL.CREW_CHANGE]),
  waitingOrders: Boolean(s[NATURE_OF_CALL.WAITING_ORDERS]),
  survey: Boolean(s[NATURE_OF_CALL.SURVEY]),
  repairs: Boolean(s[NATURE_OF_CALL.REPAIRS]),
  projects: Boolean(s[NATURE_OF_CALL.PROJECTS]),
  underwaterInspection: Boolean(s[NATURE_OF_CALL.UNDERWATER_INSPECTION]),
  hullCleaning: Boolean(s[NATURE_OF_CALL.HULL_CLEANING]),
  storesProvision: Boolean(s[NATURE_OF_CALL.STORES_PROVISION]),
});

type VesselCallFormStateWithPrefill = VesselCallFormState & {
  vesselName?: string;
};

export const mapVesselCallStoreToCreateRequest = (
  s: VesselCallFormState,
): VesselCallCreateRequest => {
  const state = s as VesselCallFormStateWithPrefill;
  const imo2020Compliance =
    s[FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE] === methodAtImo2020ComplianceValues.compliantFuel;
  const shouldIncludeCargoOnboard = shouldShowCargoOnboardSection(s[FORM_FIELDS.VESSEL_CONDITION]);
  const cargoOnboard = shouldIncludeCargoOnboard
    ? mapCargoOnboardFormToApi(s[FORM_FIELDS.CARGO_ONBOARD])
    : null;

  return {
    vesselId: s[FORM_FIELDS.VESSEL_ID] ?? "",
    vesselName:
      state.vesselName || (s[FORM_FIELDS.NAME_AND_TYPE_OF_SHIP] as string | undefined) || "",
    companyId: undefined as unknown as string,

    selectPortReferenceCode: s[FORM_FIELDS.SELECT_PORT] ?? "",
    expectedArrivalLocation: s[FORM_FIELDS.EXPECTED_ARRIVAL_LOCATION] ?? "",
    preferredAnchorageArea: {
      anchorageAreaCode: s[FORM_FIELDS.PREFERRED_ANCHORAGE_AREA] ?? "",
    },
    reasonForAnchoragePreference: s[FORM_FIELDS.REASON_FOR_ANCHORAGE_PREFERENCE] ?? undefined,
    masterName: s[FORM_FIELDS.MASTERS_NAME] || s[FORM_FIELDS.NAME_OF_MASTER] || "",
    nationality: s[FORM_FIELDS.NATIONALITY] ?? "",

    eta: toISODateTime(s[FORM_FIELDS.ETA]) ?? "",
    etd: toISODateTime(s[FORM_FIELDS.ETD]) ?? "",

    ownersMattersAgent: s[FORM_FIELDS.OWNERS_MATTERS_AGENT] ?? "",

    lastPortReferenceCode: s[FORM_FIELDS.LAST_PORT] ?? "",
    nextPortReferenceCode: s[FORM_FIELDS.NEXT_PORT] ?? "",

    draftFwd: toNullableNumber(s[FORM_FIELDS.DRAFT_FWD]),
    draftAft: toNullableNumber(s[FORM_FIELDS.DRAFT_AFT]),
    vesselConditionReferenceCode: s[FORM_FIELDS.VESSEL_CONDITION] ?? "",

    cargoOnboard,

    natureOfCall: mapNatureOfCall(s),
    imo2020Compliance,

    portFacility: s[FORM_FIELDS.PORT_FACILITY] ?? "",
    portFacilitySpecify: s[FORM_FIELDS.PORT_FACILITY_OTHER] ?? "",

    securityLevel: s[FORM_FIELDS.SECURITY_LEVEL] ?? "",

    isscValid: Boolean(s[FORM_FIELDS.ISSC_IS_VALID]),
    isscNumber: s[FORM_FIELDS.ISSC_NUMBER] ?? "",
    isscIssueDate: toISODate(s[FORM_FIELDS.ISSC_ISSUE_DATE]) ?? "",
    isscExpiryDate: toISODate(s[FORM_FIELDS.ISSC_EXPIRY_DATE]) ?? "",
    isscIssuedBy: s[FORM_FIELDS.ISSC_ISSUED_BY] ?? "",

    sspOnboardApproved: Boolean(s[FORM_FIELDS.APPROVED_SSP_ONBOARD]),

    portCalls: (s.portCalls ?? []).map((pc) => ({
      portReferenceCode: pc.port ?? "",
      countryReferenceCode: pc.country ?? "",
      arrivalDate: toISODate(pc.arrivalDate) ?? "",
      departureDate: toISODate(pc.departureDate) ?? "",
      securityLevel: pc.securityLevelOfPortCall ?? "",
      internalNotes: pc.comments ?? "",
    })),

    shipPortSecurityMeasuresTaken: Boolean(s[FORM_FIELDS.SPECIAL_SECURITY_MEASURES]),
    takenMeasures: s[FORM_FIELDS.TAKEN_MEASURES] ?? "",

    securityGuardsOnBoard: Boolean(s[FORM_FIELDS.SECURITY_GUARDS_ON_BOARD]),
    securityGuardsNumber: toNullableInt(s[FORM_FIELDS.NUMBER_OF_SECURITY_GUARDS]),

    flagStatePermissionDocumentUrl:
      s[FORM_FIELDS.FLAG_STATE_PERMISSION_DOCUMENT_URL]?.[0]?.url ?? "",

    armAmmunitionOnboard: Boolean(s[FORM_FIELDS.ARM_AMMUNITION_ON_BOARD]),
    armAmmunitionProofDocumentUrl: s[FORM_FIELDS.ARM_AMMUNITION_PROOF_DOCUMENT_URL]?.[0]?.url ?? "",

    vesselHasDeficiencies: Boolean(s[FORM_FIELDS.VESSEL_HAS_DEFICIENCIES]),
    deficiencies: s[FORM_FIELDS.DEFICIENCIES] ?? "",
    deficienciesDocumentsUrl: s[FORM_FIELDS.DEFICIENCIES_DOCUMENTS_URL]?.[0]?.url ?? "",
    lastPortClearanceDocumentUrl: s[FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL]?.[0]?.url ?? "",
    lastPortClearanceDocumentName: s[FORM_FIELDS.LAST_PORT_CLEARANCE_DOCUMENT_URL]?.[0]?.name ?? "",
    lastPortClearanceIssueDate: toISODate(s[FORM_FIELDS.LAST_PORT_CLEARANCE_ISSUE_DATE]) ?? "",
    lastPortClearanceClassName: s[FORM_FIELDS.LAST_PORT_CLEARANCE_CLASS_NAME] ?? "",

    generalDeclaration: {
      nameAndTypeOfShip: s[FORM_FIELDS.NAME_AND_TYPE_OF_SHIP] ?? "",
      imoNumber: toIntOrZero(s[FORM_FIELDS.IMO_NUMBER]),
      callSign: s[FORM_FIELDS.CALL_SIGN] ?? "",
      voyageNumber: s[FORM_FIELDS.VOYAGE_NUMBER] ?? "",
      arrivalPortReferenceCode: s[FORM_FIELDS.PORT_OF_ARRIVAL] ?? "",
      arrivalDateTime: toISODateTime(s[FORM_FIELDS.DATE_TIME_OF_ARRIVAL]) ?? "",
      flagStateOfShip: s[FORM_FIELDS.FLAG_STATE_OF_SHIP] ?? "",
      masterName: s[FORM_FIELDS.NAME_OF_MASTER] ?? "",
      lastPortOfCallReferenceCode: s[FORM_FIELDS.LAST_PORT_OF_CALL] ?? "",
      registryCertificate: s[FORM_FIELDS.CERTIFICATE_OF_REGISTRY] ?? "",
      shipAgentDetails: s[FORM_FIELDS.SHIP_AGENT_CONTACT] ?? "",
      grossTonnage: toNullableInt(s[FORM_FIELDS.GROSS_TONNAGE]),
      netTonnage: toNullableInt(s[FORM_FIELDS.NET_TONNAGE]),
      voyageDetails: s[FORM_FIELDS.VOYAGE_PARTICULARS] ?? "",
      cargoDescription: s[FORM_FIELDS.CARGO_DESCRIPTION] ?? "",
      crewNumber: toNullableInt(s[FORM_FIELDS.NUMBER_OF_CREW]),
      passengersNumber: toNullableInt(s[FORM_FIELDS.NUMBER_OF_PASSENGERS]),
      remarks: s[FORM_FIELDS.REMARKS] ?? "",
      wasteShipRequirements: s[FORM_FIELDS.WASTERESIDUES_REQUIREMENTS] ?? "",
      completedAndSignedDocumentUrl: s[FORM_FIELDS.COMPLETED_SIGNED_DOCUMENT_URL]?.[0]?.url ?? "",
    },

    crews: (s.crew ?? []).map((m, index) => ({
      no: toPositiveOrder(m.order, index),
      familyName: m.familyName ?? "",
      givenName: m.givenName ?? "",
      rankReferenceCode: m.rank ?? "",
      nationality: m.nationality ?? "",
      birthPlace: m.placeOfBirth ?? "",
      birthDate: toISODate(m.dateOfBirth) ?? "",
      gender: m.gender ?? "",
      passportNumber: m.passportNumber ?? "",
      identityDocumentIssuingStateReferenceCode: m.issuingState ?? "",
      identityDocumentExpiryDate: toISODate(m.expiryDate) ?? "",
    })),

    passengers: (s.passengers ?? []).map((p, index) => ({
      no: toPositiveOrder(p.order, index),
      familyName: p.familyName ?? "",
      givenName: p.givenName ?? "",
      nationality: p.nationality ?? "",
      birthDate: toISODate(p.dateOfBirth) ?? "",
      birthPlace: p.placeOfBirth ?? "",
      gender: p.gender ?? "",
      identityOrTravelDocumentTypeReferenceCode: p.typeOfIdentityDocument ?? "",
      identityOrTravelDocumentSerialNumber: p.identityDocumentNumber ?? "",
      identityOrTravelDocumentIssuingStateReferenceCode: p.issuingState ?? "",
      identityOrTravelDocumentExpiryDate: toISODate(p.expiryDate) ?? "",
      visaNumber: p.visaNumberIfAppropriate ?? "",
      embarkationPortReferenceCode: p.portOfEmbarkation ?? "",
      disembarkationPortReferenceCode: p.portOfDisembarkation ?? "",
      transitPassenger: Boolean(p.transitPassenger),
    })),

    cargoDeclarationDocumentUrl: s[FORM_FIELDS.CARGO_DECLARATION_DOCUMENT_URL]?.[0]?.url ?? "",
    shipsStoresDeclarationDocumentUrl:
      s[FORM_FIELDS.SHIPS_STORE_DECLARATION_DOCUMENT_URL]?.[0]?.url ?? "",
    crewsEffectsDeclarationDocumentUrl:
      s[FORM_FIELDS.CREWS_EFFECTS_DECLARATION_DOCUMENT_URL]?.[0]?.url ?? "",
    dangerousGoodsManifestDocumentUrl:
      s[FORM_FIELDS.DANGEROUS_GOODS_MANIFEST_DOCUMENT_URL]?.[0]?.url ?? "",
    additionalRemarks: s[FORM_FIELDS.ADDITIONAL_REMARKS] ?? "",
    additionalDocuments: Array.isArray(s[FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS] ?? [])
      ? (s[FORM_FIELDS.CALL_ADDITIONAL_DOCUMENTS] ?? []).map((doc) => {
          const documentName = doc.documentName ?? "";
          const type = mapAdditionalDocumentType(doc.documentType);
          const specify = mapAdditionalDocumentSpecify(type, doc.otherSpecifyType);
          const issueDate = toISODate(doc.issueDate);
          const expiryDate = toISODate(doc.expiryDate);
          const summary = doc.documentSummary?.trim();

          return {
            url: doc.documentUrl ?? "",
            name: documentName,
            type,
            originalFileName: doc.fileName ?? documentName,
            fileFormat: mapFileFormat(doc.fileFormat, doc.fileName ?? documentName),
            specify,
            ...(issueDate ? { issueDate } : {}),
            ...(expiryDate ? { expiryDate } : {}),
            ...(summary ? { summary } : {}),
          };
        })
      : [],
  };
};
